export type ShareQuality = 'auto' | 'fluido' | 'nitido'

/**
 * Teto de resolucao escolhido por QUEM TRANSMITE.
 *
 * Existe porque o custo de uma transmissao e quase todo quadratico na altura:
 * capturar um monitor 4K e codifica-lo custa quatro vezes o que custa 1080p —
 * na CPU de quem manda, na banda do caminho e no decoder de cada pessoa que
 * assiste. Sem teto, o navegador entrega a resolucao nativa da fonte e quem
 * transmite so descobre o problema quando a imagem ja esta engasgando.
 */
export type ShareResolution = 'nativa' | '1440p' | '1080p' | '720p' | '540p'

export interface ResolutionPreset {
    label: string
    description: string
    /** Altura maxima em px. `null` = sem teto (a fonte manda). */
    height: number | null
}

export const RESOLUTION_PRESETS: Record<ShareResolution, ResolutionPreset> = {
    nativa: {
        label: 'Nativa',
        description: 'A tela do jeito que ela e — pesado num monitor grande',
        height: null
    },
    '1440p': {
        label: '1440p',
        description: 'Monitor grande, se a maquina aguentar',
        height: 1440
    },
    '1080p': {
        label: '1080p',
        description: 'Full HD — o padrao; o equilibrio de sempre',
        height: 1080
    },
    '720p': {
        label: '720p',
        description: 'Leve; bom pra maquina fraca ou rede ruim',
        height: 720
    },
    '540p': {
        label: '540p',
        description: 'O minimo que ainda da pra ler',
        height: 540
    }
}

/** Ordem em que as opcoes aparecem no menu — da mais pesada pra mais leve. */
export const RESOLUTION_ORDER: ShareResolution[] = ['nativa', '1440p', '1080p', '720p', '540p']

/**
 * Padrao: 1080p.
 *
 * Ja foi `nativa`, e o custo apareceu em quem transmite: o encoder trabalha
 * por pixel e por quadro, e um monitor 1440p a 60 fps e 3,5x o trabalho de
 * 1080p a 30 — 8x num 4K. Na malha ainda e um encoder por espectador, e o
 * VP8 roda no processador: a maquina inteira travava. Quem mostra texto miudo
 * num monitor grande sobe pra `nativa` no menu.
 */
export const DEFAULT_RESOLUTION: ShareResolution = '1080p'

/** Referencia do orcamento de banda: os tetos dos presets valem pra 1920x1080. */
const BUDGET_REFERENCE_PIXELS = 1920 * 1080

/**
 * Teto absoluto do orcamento, em bits/s, por maior que seja a fonte.
 *
 * Acompanhar os pixels de um monitor 4K a 60 fps pediria 40 Mbps — mais do que
 * o upload da maioria das casas, e acima do que um encoder em tempo real
 * converte em imagem visivelmente melhor.
 */
const MAX_UPLOAD_BUDGET = 20_000_000

export interface QualityPreset {
    label: string
    description: string
    frameRate: number
    /** Dica pro encoder priorizar movimento ou legibilidade. */
    contentHint: 'motion' | 'detail' | ''
    /**
     * Orcamento TOTAL de upload de video, em bits/s, a 1080p.
     *
     * Total, e nao por conexao: na malha P2P uma transmissao sai uma vez para
     * cada espectador, entao um teto "por conexao" de 6 Mbps vira 30 Mbps de
     * upload numa sala de seis. Praticamente ninguem tem essa banda, e o que
     * acontecia na pratica era congestionamento — perda de pacote e imagem
     * travando pra sala inteira, que e pior do que ter limitado. Quem divide
     * este numero pelo numero de conexoes e o transporte.
     *
     * Nenhum preset deixa isto em zero. "Sem teto" nao existe no Chrome: sem
     * `maxBitrate`, ele aplica o proprio teto de ~2,5 Mbps a qualquer video
     * acima de 960x540 — e era por isso que o "Auto" deixava 1080p borrado.
     */
    maxBitrate: number
}

export const QUALITY_PRESETS: Record<ShareQuality, QualityPreset> = {
    auto: {
        label: 'Auto',
        description: '30 fps — o padrao no processador; equilibrio entre nitidez e fluidez',
        frameRate: 30,
        contentHint: '',
        maxBitrate: 6_000_000
    },
    fluido: {
        label: 'Fluido',
        description: '60 fps — o padrao com GPU; bom pra jogo e video',
        frameRate: 60,
        contentHint: 'motion',
        maxBitrate: 10_000_000
    },
    nitido: {
        label: 'Nitido',
        description: '15 fps — bom pra texto e codigo',
        frameRate: 15,
        contentHint: 'detail',
        maxBitrate: 3_000_000
    }
}

/**
 * Padrao no processador: 30 fps. Os 60 do Fluido dobram o trabalho do
 * encoder, e com VP8 num encoder por espectador era isso que travava a
 * maquina de quem transmite.
 */
export const DEFAULT_QUALITY: ShareQuality = 'auto'

/**
 * Qualidade de partida enquanto a pessoa nao escolhe uma no menu: 60 fps
 * quando a placa de video codifica, 30 quando e o processador.
 *
 * Com a GPU o custo dos 60 fps sai da placa, nao da CPU — o motivo que tinha
 * trazido o padrao pra 30 nao vale ali.
 */
export function defaultQuality(gpuEncodes: boolean): ShareQuality {
    return gpuEncodes ? 'fluido' : DEFAULT_QUALITY
}

/** Tamanho que a fonte esta entregando agora, quando ja se sabe. */
export interface SourceSize {
    width: number
    height: number
}

/**
 * Orcamento de upload pra combinacao escolhida, em bits/s.
 *
 * A banda que uma imagem precisa acompanha a quantidade de pixels: cair de
 * 1080p pra 540p e um quarto dos pixels, e insistir no orcamento cheio ali so
 * gastaria banda sem melhorar nada visivel. Vale igual pra cima — um monitor
 * 1440p tem 1,8x os pixels de 1080p, e com o mesmo orcamento o encoder so
 * consegue entrega-lo mais borrado que o 1080p.
 *
 * `source` e o tamanho real da fonte: sem ele, `nativa` nao tem como saber o
 * que vai entrar e usa a referencia de 1080p ate a captura existir.
 */
export function uploadBudget(
    quality: ShareQuality,
    resolution: ShareResolution,
    source?: SourceSize | null
): number {
    const budget = QUALITY_PRESETS[quality].maxBitrate
    if (budget <= 0) return 0
    const pixels = budgetPixels(resolution, source)
    return Math.min(MAX_UPLOAD_BUDGET, Math.round(budget * (pixels / BUDGET_REFERENCE_PIXELS)))
}

/** Pixels que o teto deixa passar — o menor entre a fonte e o teto. */
function budgetPixels(resolution: ShareResolution, source?: SourceSize | null): number {
    const curto = RESOLUTION_PRESETS[resolution].height
    const teto = curto === null ? null : curto * Math.round((curto * 16) / 9)
    if (source && source.width > 0 && source.height > 0) {
        const pixels = source.width * source.height
        return teto === null ? pixels : Math.min(pixels, teto)
    }
    return teto ?? BUDGET_REFERENCE_PIXELS
}

/**
 * O que o encoder sacrifica quando nao da conta de tudo, ou `undefined` pra
 * deixar o navegador decidir.
 *
 * So o Nitido (`contentHint = 'detail'`) segura a RESOLUCAO a qualquer custo:
 * ali a pessoa escolheu texto legivel, e 15 fps ja deixam folga de CPU.
 *
 * O resto da tela vai em `balanced`. `maintain-resolution` em 30 ou 60 fps
 * deixava o encoder sem saida: sem CPU, ele so podia cortar quadros, e cada
 * quadro seguia no tamanho cheio — a maquina ficava no limite e a imagem
 * engasgava. `balanced` deixa ceder um pouco dos dois. E tambem nao e o
 * `maintain-framerate` que a dica `motion` faria o Chrome escolher sozinho,
 * que encolhia 1920x1080 pra 1440x810 na primeira falta de banda.
 *
 * Camera fica com o navegador: rosto em resolucao menor continua rosto, e la
 * travar e que incomoda.
 *
 * A tela se reconhece pelo `displaySurface`, que so trilha de captura de tela
 * tem. Navegador que nao o informa cai no comportamento do navegador.
 */
export function degradationFor(track: MediaStreamTrack): RTCDegradationPreference | undefined {
    if (!track.getSettings?.().displaySurface) return undefined
    return track.contentHint === 'detail' ? 'maintain-resolution' : 'balanced'
}

/** De pe ou deitada — quem define o teto de cada lado. */
export type Orientation = 'landscape' | 'portrait'

/**
 * A orientacao em que uma trilha esta entregando agora.
 *
 * Deitada e a resposta quando nao da pra saber — e a forma de quase toda fonte,
 * e errar pra ca so mantem o comportamento que sempre existiu.
 */
export function trackOrientation(track: MediaStreamTrack): Orientation {
    const { width = 0, height = 0 } = track.getSettings?.() ?? {}
    return height > width ? 'portrait' : 'landscape'
}

/**
 * Constraints de tamanho pro teto escolhido.
 *
 * `max`, nunca `ideal`: `ideal` faz o navegador ESTICAR uma fonte menor ate o
 * alvo, gastando encoder pra inventar pixel que nao existe. O que se quer aqui
 * e so nao passar do teto.
 *
 * O teto vale pro lado CURTO, que e o que "1080p" sempre quis dizer. Sem a
 * orientacao, o antigo `height: { max }` fixo cortava o lado LONGO de uma
 * fonte em pe: uma camera 4K de celular (2160x3840) virava 608x1080 — um
 * terco dos pixels que o preset promete, imagem mole e bitrate pela metade.
 * Ver `docs/orientacao-de-video.md`.
 */
export function sizeConstraints(
    resolution: ShareResolution,
    orientation: Orientation = 'landscape'
): MediaTrackConstraints {
    const curto = RESOLUTION_PRESETS[resolution].height
    if (curto === null) return {}
    // Limitar os dois lados, e nao so o curto, evita que uma fonte ultrawide
    // entre com 3440 px de largura por ter altura pequena.
    const longo = Math.round((curto * 16) / 9)
    return orientation === 'portrait'
        ? { width: { max: curto }, height: { max: longo } }
        : { width: { max: longo }, height: { max: curto } }
}

/**
 * Teto generoso pra ABRIR a fonte, antes de saber como ela entrega.
 *
 * A orientacao so aparece depois que a trilha existe, e pedir o teto deitado
 * de cara ja esmagaria uma camera em pe. Este teto cabe nas duas; `fitToSource`
 * aperta em seguida, com a orientacao na mao.
 */
function openingConstraints(resolution: ShareResolution): MediaTrackConstraints {
    const curto = RESOLUTION_PRESETS[resolution].height
    if (curto === null) return {}
    const longo = Math.round((curto * 16) / 9)
    return { width: { max: longo }, height: { max: longo } }
}

/**
 * Aperta a trilha recem-aberta pro teto certo da orientacao dela.
 *
 * Sem isto, uma camera 4:3 entraria em 1920x1440 (quase 3 MP) num preset que
 * pediu 1080p. Falhar aqui nao tira a camera de ninguem: a fonte ja esta no
 * ar, so um pouco maior que o pedido.
 */
async function fitToSource(
    stream: MediaStream,
    resolution: ShareResolution,
    frameRate: number
): Promise<void> {
    for (const track of stream.getVideoTracks()) {
        // Em volta de TUDO, e nao so do `applyConstraints`: a fonte ja esta no
        // ar aqui, e deixar qualquer tropeco daqui escapar trocaria uma imagem
        // grande demais por imagem nenhuma.
        try {
            const alvo = sizeConstraints(resolution, trackOrientation(track))
            if (!alvo.width && !alvo.height) continue
            await track.applyConstraints({ frameRate: { ideal: frameRate }, ...alvo })
        } catch {
            // Segue com o que a fonte deu.
        }
    }
}

/** Por que a tela ou a camera esta bloqueada agora — null quando esta liberada. */
export type MediaBlockReason = 'insecure-context' | 'unsupported'

/**
 * Contexto inseguro (http fora de localhost) derruba `mediaDevices` antes do
 * navegador ser perguntado. Sem separar este caso do resto, quem acessa por
 * IP de rede local (comum ao testar do celular na mesma casa) recebe a mesma
 * mensagem de "troque de navegador" de quem usa um navegador antigo — e o
 * que falta ali e HTTPS, nao um navegador diferente.
 */
function insecureContext(): boolean {
    return typeof window !== 'undefined' && window.isSecureContext === false
}

function blockReason(apiPresent: boolean): MediaBlockReason | null {
    if (apiPresent) return null
    return insecureContext() ? 'insecure-context' : 'unsupported'
}

/** Camera disponivel? Exige contexto seguro (HTTPS ou localhost). */
export function cameraSupported(): boolean {
    return cameraBlockReason() === null
}

/** Motivo de a camera estar bloqueada agora, ou null se ela funcionar. */
export function cameraBlockReason(): MediaBlockReason | null {
    const apiPresent = typeof navigator !== 'undefined'
        && !!navigator.mediaDevices
        && typeof navigator.mediaDevices.getUserMedia === 'function'
    return blockReason(apiPresent)
}

/** Texto pronto pro tooltip do botao de camera — null quando ela funciona. */
export function cameraUnavailableMessage(): string | null {
    switch (cameraBlockReason()) {
        case 'insecure-context':
            return 'Este site precisa de HTTPS (ou localhost) pra acessar a camera'
        case 'unsupported':
            return 'Seu navegador ou dispositivo nao da acesso a camera'
        default:
            return null
    }
}

/**
 * Cameras disponiveis. Os rotulos so aparecem depois que a pessoa concede a
 * permissao uma vez — antes disso o navegador devolve string vazia, e por isso
 * a UI cai num "Camera 1", "Camera 2".
 */
export async function listCameras(): Promise<MediaDeviceInfo[]> {
    if (!cameraSupported() || typeof navigator.mediaDevices.enumerateDevices !== 'function') return []
    try {
        const devices = await navigator.mediaDevices.enumerateDevices()
        return devices.filter((device) => device.kind === 'videoinput')
    } catch {
        return []
    }
}

/**
 * Abre a camera. Sem microfone de proposito: a sala e pra mostrar, e audio de
 * mic traria eco e mixagem — a conversa continua onde ja acontece.
 */
export async function captureCamera(
    quality: ShareQuality,
    deviceId?: string,
    resolution: ShareResolution = DEFAULT_RESOLUTION
): Promise<MediaStream> {
    const preset = QUALITY_PRESETS[quality]
    const stream = await navigator.mediaDevices.getUserMedia({
        video: {
            frameRate: { ideal: preset.frameRate },
            /**
             * O teto escolhido no menu, e nao mais um `ideal: 1920x1080` fixo.
             *
             * Pedir Full HD de uma webcam custa caro no caminho inteiro — o
             * canvas do efeito de fundo, um encoder por espectador na malha, o
             * decoder de cada pessoa que assiste — pra um tile que raramente
             * passa de 400 px de largura na tela do outro lado.
             */
            ...openingConstraints(resolution),
            ...(deviceId ? { deviceId: { exact: deviceId } } : {})
        },
        audio: false
    })

    await fitToSource(stream, resolution, preset.frameRate)

    // Rosto em movimento pede fluidez, nao nitidez de texto: o preset "nitido"
    // (15 fps, pensado pra codigo) deixaria a imagem travada.
    for (const track of stream.getVideoTracks()) {
        track.contentHint = preset.contentHint === 'detail' ? 'motion' : (preset.contentHint || 'motion')
    }
    return stream
}

export function screenShareSupported(): boolean {
    return screenShareBlockReason() === null
}

/** Motivo de a captura de tela estar bloqueada agora, ou null se ela funcionar. */
export function screenShareBlockReason(): MediaBlockReason | null {
    const apiPresent = typeof navigator !== 'undefined'
        && !!navigator.mediaDevices
        && typeof navigator.mediaDevices.getDisplayMedia === 'function'
    return blockReason(apiPresent)
}

/** Texto pronto pro tooltip do botao de compartilhar tela — null quando ela funciona. */
export function screenShareUnavailableMessage(): string | null {
    switch (screenShareBlockReason()) {
        case 'insecure-context':
            return 'Este site precisa de HTTPS (ou localhost) pra compartilhar a tela'
        case 'unsupported':
            // Na pratica isto e sempre celular: todo navegador de desktop atual
            // captura tela, e nenhum de celular captura — nem trocando de app,
            // porque no iOS todos usam o mesmo motor do Safari.
            return 'Captura de tela nao existe em navegador de celular. Abra a sala num computador pra transmitir.'
        default:
            return null
    }
}

/**
 * Onde a pessoa esta — so o que muda a resposta sobre audio de tela.
 *
 * Nao e curiosidade: quem captura som do SISTEMA e o SO, e so Windows e
 * ChromeOS deixam. Num Mac ou num Linux a mesma escolha de "tela inteira"
 * devolve video mudo, sem erro nenhum, e sem separar os dois casos o aviso
 * mandaria a pessoa procurar uma caixinha que o navegador dela nunca mostra.
 */
export type SharePlatform = 'windows' | 'chromeos' | 'macos' | 'linux' | 'outro'

export function sharePlatform(): SharePlatform {
    if (typeof navigator === 'undefined') return 'outro'
    const data = (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData
    // Os tres juntos porque nenhum sozinho e confiavel: `userAgentData` so
    // existe no Chromium, `platform` esta depreciado e o UA mente por design.
    const raw = `${data?.platform ?? ''} ${navigator.platform ?? ''} ${navigator.userAgent ?? ''}`.toLowerCase()
    // ChromeOS antes de linux: o UA dele diz "CrOS ... Linux", e a resposta
    // sobre audio e a oposta.
    if (raw.includes('cros')) return 'chromeos'
    if (raw.includes('win')) return 'windows'
    if (raw.includes('mac')) return 'macos'
    if (raw.includes('linux') || raw.includes('x11')) return 'linux'
    return 'outro'
}

/** O navegador e da familia Chromium? So ela entrega audio de tela na pratica. */
function chromiumBased(): boolean {
    if (typeof navigator === 'undefined') return false
    const data = (navigator as Navigator & { userAgentData?: { brands?: { brand: string }[] } }).userAgentData
    if (data?.brands?.length) {
        return data.brands.some((item) => /chromium|google chrome|microsoft edge/i.test(item.brand))
    }
    const ua = navigator.userAgent ?? ''
    return /chrome|chromium|edg\//i.test(ua) && !/firefox|fxios/i.test(ua)
}

/**
 * Por que esta captura de tela veio sem som, e o que fazer a respeito.
 *
 * `getDisplayMedia({ audio: true })` NUNCA falha quando o navegador nao pode
 * dar audio: ele devolve a stream so com video, calado. Sem este texto, o
 * unico sinal que a pessoa recebe e um botao que nao aparece — dos dois lados,
 * porque quem assiste tambem so ganha o icone de som quando ha trilha. O
 * resultado e "compartilhei com audio e nao funcionou", sem nada no console.
 *
 * O texto muda com a plataforma porque a instrucao certa muda com ela: mandar
 * um usuario de Mac marcar "compartilhar audio do sistema" e mandar procurar
 * uma caixinha que nao existe ali.
 */
export function screenAudioMissingMessage(): string {
    if (!chromiumBased()) {
        return 'Esta transmissao entrou sem som: este navegador praticamente nao '
            + 'entrega audio de tela. No Chrome ou no Edge, escolha uma guia e '
            + 'marque "Compartilhar audio da guia".'
    }
    switch (sharePlatform()) {
        case 'windows':
        case 'chromeos':
            return 'Esta transmissao entrou sem som. Pra mandar audio junto, escolha '
                + 'uma guia e marque "Compartilhar audio da guia", ou a tela inteira '
                + 'e marque "Compartilhar audio do sistema". Janela de aplicativo '
                + 'nunca traz som.'
        case 'macos':
            return 'Esta transmissao entrou sem som. No Mac o Chrome nao captura o som '
                + 'do sistema: o audio so vai junto quando voce escolhe uma guia do '
                + 'navegador e marca "Compartilhar audio da guia".'
        case 'linux':
            return 'Esta transmissao entrou sem som. No Linux o Chrome nao captura o som '
                + 'do sistema: o audio so vai junto quando voce escolhe uma guia do '
                + 'navegador e marca "Compartilhar audio da guia".'
        default:
            return 'Esta transmissao entrou sem som. O audio so vai junto quando voce '
                + 'escolhe uma guia e marca "Compartilhar audio da guia" no seletor.'
    }
}

/**
 * Abre o seletor de tela do navegador e devolve a stream ja etiquetada
 * com a dica de conteudo do preset escolhido.
 *
 * O audio pedido aqui e o da MAQUINA (aba ou sistema), nao o microfone — esse
 * e outro fluxo, aberto so quando a pessoa entra na voz.
 */
export async function captureScreen(
    quality: ShareQuality,
    resolution: ShareResolution = DEFAULT_RESOLUTION
): Promise<MediaStream> {
    const preset = QUALITY_PRESETS[quality]
    const stream = await navigator.mediaDevices.getDisplayMedia({
        /**
         * Sem o teto, um monitor 4K entra com 3840x2160: quatro vezes os
         * pixels de 1080p pra codificar, transmitir e decodificar. Em 60 fps
         * nenhuma CPU domestica da conta e o encoder degrada sozinho,
         * derrubando quadros — o teto entrega mais imagem, nao menos.
         */
        video: { frameRate: { ideal: preset.frameRate }, ...openingConstraints(resolution) },
        /**
         * Audio da aba (ou do sistema) quando o navegador oferecer; sem ele a
         * captura segue igual, so muda.
         *
         * `true` puro, e nao um objeto de constraints: e a unica forma que todo
         * navegador aceita em captura de tela. Pedindo constraints, quem nao as
         * suporta pode devolver a captura SEM a trilha de audio, em silencio —
         * e ai a preferencia custa justamente aquilo que se queria. As
         * preferencias vao depois, no `applyConstraints`, onde falhar nao tira
         * nada de ninguem.
         */
        audio: true,
        /**
         * Fora do padrao, so o Chrome entende: garante o som do sistema junto
         * quando a escolha e "tela inteira". Quem nao conhece a opcao ignora,
         * e por isso ela entra por asercao — a lib do TypeScript ainda nao a
         * tem em `DisplayMediaStreamOptions`.
         */
        systemAudio: 'include'
    } as DisplayMediaStreamOptions)

    // Monitor em pe existe, e nele o teto deitado cortaria o lado longo.
    await fitToSource(stream, resolution, preset.frameRate)

    if (preset.contentHint) {
        for (const track of stream.getVideoTracks()) track.contentHint = preset.contentHint
    }
    for (const track of stream.getAudioTracks()) {
        // Tira o Opus do modo de voz: sem esta dica ele otimiza pra fala, corta
        // o agudo e derruba o estereo — justamente o que se perde ao mostrar um
        // video.
        track.contentHint = 'music'

        /**
         * Desliga o processamento de microfone.
         *
         * Ligado, o navegador trata a trilha como voz: cancelamento de eco,
         * supressao de ruido e ganho automatico deixam musica e video abafados,
         * com trechos sumindo quando alguem fala perto do microfone de quem
         * transmite. A fonte aqui ja e um sinal limpo saindo da propria
         * maquina.
         *
         * Best-effort de proposito: navegador que nao conhece estas opcoes
         * recusa a promessa, e a trilha continua no ar do mesmo jeito — so com
         * o processamento que ele achar melhor.
         */
        void track.applyConstraints({
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl: false
        }).catch(() => undefined)
    }
    return stream
}

export function stopStream(stream: MediaStream | null): void {
    if (!stream) return
    for (const track of stream.getTracks()) track.stop()
}
